import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { AlertTriangle, ClipboardCopy, Check } from 'lucide-react';
import { PageHeader, Skeleton } from '@/components/ui';
import { buildDiagnosticsReport, type DiagnosticsReport } from '@/db/diagnostics';
import { logger } from '@/lib/logger';
import {
  clearAppCache,
  factoryReset,
  rebuildLocalIndex,
  resetCatalogOnly,
  type MaintenanceResult,
} from '@/services/maintenance/recovery';

export function DiagnosticsPage(): React.ReactElement {
  const [report, setReport] = useState<DiagnosticsReport | null>(null);
  const [result, setResult] = useState<MaintenanceResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);

  const logs = useSyncExternalStore(logger.subscribe, logger.list, logger.list);

  const refresh = useCallback(async () => {
    setReport(await buildDiagnosticsReport());
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const run = useCallback(
    async (action: () => Promise<MaintenanceResult>) => {
      setBusy(true);
      try {
        setResult(await action());
        await refresh();
      } catch (error) {
        logger.error('manutenção', 'ação falhou', error);
        setResult({ label: 'Falhou', detail: 'A operação não pôde ser concluída.' });
      } finally {
        setBusy(false);
      }
    },
    [refresh],
  );

  const copy = useCallback(async () => {
    if (!report) return;

    const text = JSON.stringify({ report, logs: logger.list() }, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      logger.warn('diagnóstico', 'a área de transferência não está disponível');
    }
  }, [report, logs]);

  return (
    <>
      <PageHeader title="Diagnóstico" backTo="/settings" />

      {!report ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <>
          <section className="rounded-xl bg-slate-800 p-4">
            <div className="flex items-start justify-between gap-3">
              <h2 className="font-medium">Estado da aplicação</h2>
              <button
                type="button"
                onClick={() => void copy()}
                className="flex min-h-11 shrink-0 items-center gap-1.5 rounded-lg bg-slate-700 px-3 text-sm active:bg-slate-600"
              >
                {copied ? <Check size={16} aria-hidden /> : <ClipboardCopy size={16} aria-hidden />}
                {copied ? 'Copiado' : 'Copiar'}
              </button>
            </div>

            <dl className="mt-3 space-y-1 text-sm">
              <Row label="Catálogo" value={report.catalog.present ? `v${report.catalog.catalogVersion}` : 'ausente'} />
              <Row label="Sinais" value={`${report.catalog.signs} (${report.catalog.inactiveSigns} inativos)`} />
              <Row label="Categorias" value={String(report.catalog.categories)} />
              <Row label="Favoritos" value={String(report.personal.favorites)} />
              <Row label="Aprendidos" value={String(report.personal.learned)} />
              <Row label="Downloads na fila" value={String(report.downloads.queued ?? 0)} />
              <Row label="Modo standalone" value={report.app.standalone ? 'sim' : 'não'} />
              <Row label="OPFS" value={report.capabilities.opfs ? 'disponível' : 'indisponível'} />
              <Row
                label="Persistência"
                value={
                  report.storage.persisted === null
                    ? 'não informada'
                    : report.storage.persisted
                      ? 'concedida'
                      : 'não concedida'
                }
              />
            </dl>

            <p className="mt-3 text-xs text-slate-500">
              O relatório é anônimo: contém contagens e versões, nunca quais sinais você
              favoritou ou estudou.
            </p>
          </section>

          {/* Manutenção graduada, da menos para a mais destrutiva (F20-T10). */}
          <section className="mt-3 rounded-xl bg-slate-800 p-4">
            <h2 className="font-medium">Manutenção</h2>

            <div className="mt-3 flex flex-col gap-2">
              <MaintenanceButton
                title="Reconstruir índice local"
                description="Confere banco e arquivos. Nenhum vídeo é apagado."
                disabled={busy}
                onClick={() => void run(rebuildLocalIndex)}
              />
              <MaintenanceButton
                title="Limpar cache do app"
                description="Baixa o app de novo. Catálogo, progresso e vídeos permanecem."
                disabled={busy}
                onClick={() => void run(clearAppCache)}
              />
              <MaintenanceButton
                title="Descartar catálogo local"
                description="Será rebaixado na próxima sincronização. Favoritos e vídeos permanecem."
                disabled={busy}
                onClick={() => void run(resetCatalogOnly)}
              />
            </div>

            {result && (
              <p role="status" className="mt-3 rounded-lg bg-slate-700/60 p-3 text-sm">
                <span className="font-medium">{result.label}.</span>{' '}
                <span className="text-slate-300">{result.detail}</span>
              </p>
            )}
          </section>

          {/* Reset total, visualmente separado do resto (F20-T10, F20-T11). */}
          <section className="mt-3 rounded-xl border border-red-500/30 bg-red-500/5 p-4">
            <h2 className="flex items-center gap-2 font-medium text-red-200">
              <AlertTriangle size={18} aria-hidden />
              Apagar tudo
            </h2>
            <p className="mt-1 text-sm text-slate-400">
              Remove catálogo, favoritos, progresso, fila e todos os vídeos baixados.
              Não há backup: os dados existem apenas neste dispositivo.
            </p>

            {confirmingReset ? (
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setConfirmingReset(false);
                    void run(factoryReset);
                  }}
                  className="min-h-12 flex-1 rounded-lg bg-red-500/25 text-sm font-medium text-red-100 active:bg-red-500/35 disabled:opacity-60"
                >
                  Sim, apagar tudo
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingReset(false)}
                  className="min-h-12 flex-1 rounded-lg bg-slate-700 text-sm font-medium active:bg-slate-600"
                >
                  Cancelar
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmingReset(true)}
                className="mt-3 min-h-12 w-full rounded-lg bg-slate-800 text-sm font-medium text-red-200 active:bg-slate-700"
              >
                Apagar tudo
              </button>
            )}
          </section>

          <section className="mt-3 rounded-xl bg-slate-800 p-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-medium">Registro ({logs.length})</h2>
              {logs.length > 0 && (
                <button
                  type="button"
                  onClick={() => logger.clear()}
                  className="min-h-11 rounded-lg px-3 text-sm text-slate-400 active:bg-slate-700"
                >
                  Limpar
                </button>
              )}
            </div>

            {logs.length === 0 ? (
              <p className="mt-2 text-sm text-slate-400">Nenhum evento registrado nesta sessão.</p>
            ) : (
              <ul className="mt-2 flex max-h-64 flex-col gap-1 overflow-y-auto text-xs">
                {[...logs].reverse().map((entry, index) => (
                  <li key={`${entry.at}-${index}`} className="flex gap-2">
                    <span
                      className={
                        entry.level === 'error'
                          ? 'text-red-400'
                          : entry.level === 'warn'
                            ? 'text-amber-400'
                            : 'text-slate-500'
                      }
                    >
                      [{entry.scope}]
                    </span>
                    <span className="min-w-0 break-words text-slate-300">{entry.message}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <div className="pb-6" />
        </>
      )}
    </>
  );
}

function Row({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-400">{label}</dt>
      <dd className="text-right text-slate-200">{value}</dd>
    </div>
  );
}

function MaintenanceButton({
  title,
  description,
  disabled,
  onClick,
}: {
  title: string;
  description: string;
  disabled: boolean;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="rounded-lg bg-slate-700/60 p-3 text-left active:bg-slate-700 disabled:opacity-60"
    >
      <span className="block text-sm font-medium">{title}</span>
      <span className="mt-0.5 block text-xs text-slate-400">{description}</span>
    </button>
  );
}
